/*
 * Maison Avéla — BUY pill and swipe hint.
 *
 * Scoped to two sections: the "Най-нови продукти" homepage carousel and the
 * "Може да ти хареса" PDP carousel, which intentionally reuses the exact
 * same carousel system. Everything is found from each root, and every
 * listener is attached to that root rather than to the document, so nothing
 * here can reach another, unrelated carousel elsewhere in the theme.
 *
 * Nothing in the shared carousel is touched. The drag-versus-click problem
 * is already solved upstream: assets/slideshow.js suppresses the next click
 * once a pointer has actually moved, so a drag that ends over a pill never
 * fires it.
 */
(() => {
  const ROOT = '[id$="__product_list_latest"], [id$="__product_recommendations_qggXJq"]';
  const READY = 'maBuyReady';

  /* ---------------------------------------------------------------- cart */

  /**
   * Adds one unit of a variant using the theme's own cart action. This is
   * the documented entry point: assets/standard-actions-override.js already
   * points its events at the cart drawer, so the drawer opens and the count
   * updates without us knowing anything about either.
   */
  const addToCart = async (variantId) => {
    const actions = window.Shopify && window.Shopify.actions;

    if (actions && typeof actions.updateCart === 'function') {
      await actions.updateCart({ lines: [{ merchandiseId: variantId, quantity: 1 }] });
      return;
    }

    // Only reached if the storefront is served without the standard actions
    // bundle. Falls back to the plain cart route and a page-level refresh of
    // the drawer, rather than silently doing nothing.
    await fetch((window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    document.dispatchEvent(new CustomEvent('shopify:cart:lines-update', { detail: { action: 'add' } }));
  };

  /* --------------------------------------------------------------- click */

  const onClick = async (event) => {
    const pill = event.target.closest('[data-ma-buy]');
    if (!pill) return;

    // The whole card is a link. Without this the browser follows it the
    // moment the pill is released.
    event.preventDefault();
    event.stopPropagation();

    if (pill.dataset.busy === 'true') return;

    if (pill.dataset.mode === 'choose') {
      // Hand off to the card's existing "Бърз преглед" control rather than
      // picking a variant on the customer's behalf.
      const card = pill.closest('product-card') || pill.closest('.product-card');
      const chooser = card && card.querySelector('.quick-add__button');
      if (chooser) chooser.click();
      else window.location.href = (card && card.querySelector('a[href*="/products/"]')?.href) || window.location.href;
      return;
    }

    const variantId = pill.dataset.variantId;
    if (!variantId) return;

    pill.dataset.busy = 'true';
    pill.setAttribute('aria-busy', 'true');

    try {
      await addToCart(variantId);
    } catch (error) {
      console.error('[maison-buy-pill] add to cart failed', error);
    } finally {
      pill.dataset.busy = 'false';
      pill.removeAttribute('aria-busy');
    }
  };

  /* ---------------------------------------------------------------- hint */

  /**
   * The swipe cue. Built here rather than in Liquid because the shared
   * carousel snippet renders the scroller, and that snippet must not be
   * edited. It is inert (pointer-events:none) and leaves as soon as the
   * carousel has actually moved.
   */
  const mountHint = (root) => {
    const scroller = root.querySelector('slideshow-slides');
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth + 4) return false;

    const container = scroller.closest('slideshow-container') || scroller.parentElement;
    if (!container) return false;
    if (container.querySelector('.ma-swipe')) return true;

    const hint = document.createElement('span');
    hint.className = 'ma-swipe';
    hint.setAttribute('aria-hidden', 'true');
    hint.textContent = 'SWIPE';
    container.appendChild(hint);

    let travelled = 0;
    let last = scroller.scrollLeft;

    const retire = () => {
      hint.classList.add('ma-swipe--gone');
      scroller.removeEventListener('scroll', onScroll);
      window.setTimeout(() => hint.remove(), 600);
    };

    const onScroll = () => {
      travelled += Math.abs(scroller.scrollLeft - last);
      last = scroller.scrollLeft;
      // A nudge is not an interaction. Roughly a third of a card.
      if (travelled > 60) retire();
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    return true;
  };

  /* ----------------------------------------------------------------- init */

  const init = (root) => {
    if (!root || root.dataset[READY] === 'true') return;
    root.dataset[READY] = 'true';
    root.addEventListener('click', onClick);

    // The carousel is far down the page and the shared slideshow only lays
    // its slides out once the section comes into view — before that the
    // scroller measures as not scrollable and the hint would never mount.
    // So wait for the section to actually be seen, which is also the only
    // moment the cue is any use.
    let tries = 0;
    const tryMount = () => {
      if (mountHint(root) || ++tries > 15) return true;
      window.setTimeout(tryMount, 200);
      return false;
    };

    if (!('IntersectionObserver' in window)) {
      requestAnimationFrame(tryMount);
      return;
    }

    const seen = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        seen.disconnect();
        tryMount();
      },
      { rootMargin: '200px' }
    );
    seen.observe(root);
  };

  const boot = (scope) => {
    (scope || document).querySelectorAll(ROOT).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  // The theme editor replaces the section wholesale; the replacement is a
  // fresh element without the ready flag, so this cannot double-bind.
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (section && section.matches && section.matches(ROOT)) init(section);
    else boot(section);
  });
})();
